import type { EditableGeoObject } from '@racemap/utilities/types/geos';
import type { ViewState } from 'react-map-gl/mapbox';
import { fitViewStateToBounds } from './fitViewStateToBounds';
import { getBounds } from './getBounds';
import { getDefaultViewState } from './getDefaultViewState';

/**
 * Determines the initial map view state based on the following priority:
 * 1. A custom viewport (e.g. from player options) — merged onto default values, with custom fields taking precedence.
 * 2. Bounds computed from the given GeoJSON features — the view is fitted to show all features.
 * 3. The default world view — fallback when neither a custom viewport nor features are available.
 *
 * @param customViewport - An optional partial viewport override. When provided, its fields take precedence over defaults.
 * @param features - GeoJSON features whose bounds are used to fit the view when no custom viewport is set.
 * @param viewportSize - The pixel dimensions of the map container, used for accurate bounds fitting.
 * @returns A {@link ViewState} resolved according to the priority chain above.
 */
export function resolveInitialViewState({
  customViewport,
  features,
  viewportSize,
}: {
  customViewport?: Partial<ViewState> | null;
  features?: Array<EditableGeoObject> | null;
  viewportSize?: { width: number; height: number };
}): ViewState {
  const hasCustomViewport = customViewport != null;
  const bounds = hasCustomViewport ? null : getBounds(features ?? []);

  const baseViewState = {
    ...getDefaultViewState({ withElevationChart: true }),
    ...customViewport,
  };

  return fitViewStateToBounds({
    bounds,
    baseViewState,
    viewportWidth: viewportSize?.width,
    viewportHeight: viewportSize?.height,
  });
}
